import { useEffect, useState } from 'react';
import { useIsFetching, useIsMutating, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { PublicRoutes } from '@/routes/PublicRoutes';
import { PrivateRoutes } from '@/routes/PrivateRoutes';
import { Toaster } from '@/components/ui/sonner';
import { ApiLoader } from '@/components/ui/api-loader';
import { clearAuthToken, getAuthToken, setAuthToken } from '@/api/client';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const AUTH_STORAGE_KEY = 'localfirst_admin_auth';
function App() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const activeQueries = useIsFetching();
  const activeMutations = useIsMutating();
  const isApiLoading = activeQueries > 0 || activeMutations > 0;
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(() => {
    if (typeof window === 'undefined') {
      return false;
    }

    return (
      window.localStorage.getItem(AUTH_STORAGE_KEY) === 'true' ||
      Boolean(getAuthToken())
    );
  });
  const [sessionExpiredOpen, setSessionExpiredOpen] = useState(false);

  const setAuthenticated = (value: boolean) => {
    setIsAuthenticated(value);
    window.localStorage.setItem(AUTH_STORAGE_KEY, String(value));
  };

  const handleAuthSuccess = (token?: string) => {
    setAuthenticated(true);
    if (token) {
      setAuthToken(token);
    }
    queryClient.invalidateQueries({ queryKey: ['auth', 'me'] });
    navigate('/merchants');
  };

  const handleLogout = () => {
    setAuthenticated(false);
    clearAuthToken();
    queryClient.removeQueries({ queryKey: ['auth', 'me'] });
    navigate('/login');
  };

  useEffect(() => {
    const handler = () => {
      setSessionExpiredOpen(true);
      setAuthenticated(false);
    };
    window.addEventListener('auth:expired', handler as EventListener);
    return () => window.removeEventListener('auth:expired', handler as EventListener);
  }, []);

  return (
    <>
      {!isAuthenticated ? (
        <PublicRoutes onAuthSuccess={handleAuthSuccess} />
      ) : (
        <PrivateRoutes onLogout={handleLogout} />
      )}
      <AlertDialog open={sessionExpiredOpen} onOpenChange={setSessionExpiredOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Session expired</AlertDialogTitle>
            <AlertDialogDescription>
              Your login token has expired. Please log in again to continue.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction
              onClick={() => {
                setSessionExpiredOpen(false);
                navigate('/login');
              }}
            >
              Go to login
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <div className="pointer-events-none fixed inset-0 z-50">
        <div className="relative h-full w-full">
          <ApiLoader show={isApiLoading} overlay />
        </div>
      </div>
      <Toaster position="top-center" />
    </>
  );
}

export default App;
